import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import NewItemDialog from "../modal/NewItemDialog";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import { isAxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { Textarea } from "../ui/textarea";
import { useCreateAuction } from "@/api/auction/auction";
import {
  appendNoDuplicateSaleNumbersRefine,
  auctionSchema,
} from "@/api/auction/auction-validation";

const schema = appendNoDuplicateSaleNumbersRefine(
  auctionSchema.pick({
    id: true,
    ventesDescriptionNumeroVente: true,
    ventesDescriptionTitre: true,
  }),
);

type FormType = z.infer<typeof schema>;

type NewAuctionDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const NewAuctionDialog = ({ onOpenChange, open }: NewAuctionDialogProps) => {
  const { t } = useTranslation();
  const form = useForm<FormType>({
    resolver: zodResolver(schema),
    defaultValues: {
      ventesDescriptionNumeroVente: "",
      ventesDescriptionTitre: "",
    },
    reValidateMode: "onSubmit",
  });
  const { mutateAsync } = useCreateAuction();
  const navigate = useNavigate();

  const onFormSubmit = async (data: FormType) => {
    try {
      const result = await mutateAsync(data);
      navigate(`/edit/auction/${result.id}`);
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        form.setError("ventesDescriptionNumeroVente", {
          message: t("itemWithSameIdAlreadyExists"),
        });
        return;
      }
      form.setError("root", { message: t("unknownError") });
      throw err;
    }
  };

  return (
    <NewItemDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("createAauction")}
      onFormSubmit={onFormSubmit}
      form={form}
    >
      <FormField
        control={form.control}
        name="ventesDescriptionNumeroVente"
        render={({ field }) => (
          <FormItem>
            <FormLabel required>{t("ventesDescriptionNumeroVente")}</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="ventesDescriptionTitre"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("ventesDescriptionTitre")}</FormLabel>
            <FormControl>
              <Textarea {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormMessage>{form.formState.errors.root?.message}</FormMessage>
    </NewItemDialog>
  );
};

export default NewAuctionDialog;
